/**
 * AdminRoute Component
 *
 * A route wrapper that requires admin privileges.
 * Redirects non-admins to home page and unauthenticated users to login.
 * Shows loading state while auth state is being determined.
 */

import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { PageLoader } from '@/components/ui';
import { AccessDeniedPage } from '@/components/auth/AccessDeniedPage';

/**
 * Protected route component that requires admin privileges
 *
 * Uses Outlet for nested routes pattern with React Router.
 *
 * @example
 * ```tsx
 * <Routes>
 *   <Route element={<AdminRoute />}>
 *     <Route path="/admin" element={<AdminDashboard />} />
 *     <Route path="/admin/requests" element={<GymRequestsPage />} />
 *   </Route>
 * </Routes>
 * ```
 */
export const AdminRoute = () => {
  const { isAdmin, loading, user } = useAuth();

  // Show loading state while auth state is being determined
  if (loading) {
    return <PageLoader message="Checking permissions..." />;
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Show access denied page if not an admin
  if (!isAdmin) {
    return <AccessDeniedPage />;
  }

  // Render nested routes if authenticated as admin
  return <Outlet />;
};

export default AdminRoute;
